<template>
  <section class="section" ref="section">
    <div class="columns is-centered">
      <div class="column is-one-third">
        <div class="has-text-centered" style="margin-bottom: 1rem;">
          <span style="font-size: 1.75rem; font-weight: 700; letter-spacing: -0.5px; color: #3273dc;">Fooshi</span>
        </div>
        <div class="has-text-centered" style="margin-bottom: 1.5rem;">
          <LanguageSelector />
        </div>
        <OnboardingProgress :current-step="3" />
        <h3 class="title is-4 is-spaced is-centered">
          <span>{{ $t('onboarding.accountDetails.title') }}</span>
        </h3>
        <h4 class="title is-5 is-spaced is-centered">
          <span>{{ $t('onboarding.accountDetails.subtitleOwner') }}</span>
        </h4>
        <div class="field">
          <label class="label">{{ $t('onboarding.accountDetails.ownerName') }}</label>
          <p class="control has-icons-left has-icons-right">
            <input
              class="input"
              type="text"
              placeholder=""
              autocomplete="given-name"
              v-focus
              v-model="accountDetailsOwnerInfo.ownerName"
              v-bind:class="{
                'is-danger': errors && !accountDetailsOwnerInfo.ownerName,
              }"
            />
            <span class="icon is-small is-left">
              <i class="fa fa-user"></i>
            </span>
          </p>
        </div>
        <div class="field">
          <label class="label">{{ $t('onboarding.accountDetails.ownerSurname') }}</label>
          <p class="control has-icons-left has-icons-right">
            <input
              class="input"
              type="text"
              placeholder=""
              autocomplete="family-name"
              v-model="accountDetailsOwnerInfo.ownerSurname"
              v-bind:class="{
                'is-danger': errors && !accountDetailsOwnerInfo.ownerSurname,
              }"
            />
          </p>
        </div>
        <div class="field">
          <label class="label">{{ $t('onboarding.accountDetails.address') }}</label>
          <p class="control has-icons-left has-icons-right">
            <input
              class="input"
              type="text"
              placeholder=""
              autocomplete="street-address"
              v-model="accountDetailsOwnerInfo.address"
              v-bind:class="{
                'is-danger': errors && !accountDetailsOwnerInfo.address,
              }"
            />
            <span class="icon is-small is-left">
              <i class="fa fa-address-card"></i>
            </span>
          </p>
        </div>
        <div class="field">
          <label class="label">{{ $t('onboarding.accountDetails.phone') }}</label>
          <p class="control has-icons-left has-icons-right">
            <input
              class="input"
              type="text"
              placeholder=""
              autocomplete="tel"
              v-model="accountDetailsOwnerInfo.phone"
              v-bind:class="{
                'is-danger': errors && !accountDetailsOwnerInfo.phone,
              }"
            />
            <span class="icon is-small is-left">
              <i class="fa fa-phone"></i>
            </span>
          </p>
        </div>
        <div class="field">
          <label class="label">{{ $t('onboarding.accountDetails.companyName') }}</label>
          <p class="control has-icons-left has-icons-right">
            <input
              class="input"
              type="text"
              placeholder=""
              autocomplete="organization"
              v-model="accountDetailsOwnerInfo.companyName"
              v-bind:class="{
                'is-danger': errors && !accountDetailsOwnerInfo.companyName,
              }"
            />
            <span class="icon is-small is-left">
              <i class="fa fa-building"></i>
            </span>
          </p>
        </div>
        <div class="field">
          <label class="label">{{ $t('onboarding.accountDetails.vendorVat') }}</label>
          <p class="control has-icons-left has-icons-right">
            <input
              class="input"
              type="text"
              placeholder=""
              v-model="accountDetailsOwnerInfo.vendorVat"
              v-bind:class="{
                'is-danger': errors && !accountDetailsOwnerInfo.vendorVat,
              }"
            />
            <span class="icon is-small is-left">
              <i class="fa fa-id-card"></i>
            </span>
          </p>
        </div>
        <div class="field">
          <label class="label" v-bind:class="{ 'has-text-danger': errors && accountDetailsOwnerInfo.isOss === null }">{{ $t('onboarding.accountDetails.oss') }}</label>
          <p class="help is-info" v-html="ossHelpText"></p>
          <div class="control">
            <label class="radio">
              <input type="radio" name="isOss" :value="true" v-model="accountDetailsOwnerInfo.isOss" />
              {{ $t('onboarding.accountDetails.ossYes') }}
            </label>
            <br>
            <label class="radio">
              <input type="radio" name="isOss" :value="false" v-model="accountDetailsOwnerInfo.isOss" />
              {{ $t('onboarding.accountDetails.ossNo') }}
            </label>
          </div>
        </div>
        <div class="field">
          <p class="control">
            <primary-button class="is-fullwidth" :label="$t('app.buttons.saveAndContinue')" @click="submit()" :loading="!ready"></primary-button>
          </p>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { FooshiApi } from '@/services/fooshiApi.service'
import { JwtCookie } from '../../services/jwtCookie.service.js'
import PrimaryButton from '../../components/forms/PrimaryButton.vue'
import LanguageSelector from '../../components/LanguageSelector.vue'
import OnboardingProgress from '@/components/OnboardingProgress.vue'

export default {
  components: {
    PrimaryButton,
    LanguageSelector,
    OnboardingProgress
  },
  methods: {
    async submit () {
      if (
        !this.accountDetailsOwnerInfo.ownerName ||
        !this.accountDetailsOwnerInfo.ownerSurname ||
        !this.accountDetailsOwnerInfo.address ||
        !this.accountDetailsOwnerInfo.phone ||
        !this.accountDetailsOwnerInfo.companyName ||
        !this.accountDetailsOwnerInfo.vendorVat ||
        this.accountDetailsOwnerInfo.isOss === null
      ) {
        this.errors = true
      } else {
        this.ready = false
        await this.addAccountOwnerDetails()
        this.ready = true
      }
    },
    async addAccountOwnerDetails () {
      const response = await FooshiApi.addAccountOwnerDetails({
        name: this.accountDetailsOwnerInfo.ownerName,
        surname: this.accountDetailsOwnerInfo.ownerSurname,
        address: this.accountDetailsOwnerInfo.address,
        phone: this.accountDetailsOwnerInfo.phone,
        companyName: this.accountDetailsOwnerInfo.companyName,
        vendorVat: this.accountDetailsOwnerInfo.vendorVat,
        isOss: this.accountDetailsOwnerInfo.isOss
      })
      if (response.success) {
        if (window.plausible) window.plausible('Onboarding Step Completed', { props: { stage: 'activation', step: 'account-owner' } })
        const onboardingStatus = await FooshiApi.getOnboardingStatus()
        this.step = onboardingStatus.data.step
        JwtCookie.setJwtCookie('fooshistep', this.step, 365)
        this.$router.push('terms-and-conditions')
      } else {
        // todo: toast
      }
    }
  },
  data () {
    return {
      ready: true,
      errors: false,
      step: '',
      accountDetailsOwnerInfo: {
        ownerName: '',
        ownerSurname: '',
        address: '',
        phone: '',
        companyName: '',
        vendorVat: '',
        isOss: null
      }
    }
  },
  computed: {
    ossLink () {
      const lang = this.$i18n.locale || 'en'
      return `https://europa.eu/youreurope/business/taxation/vat/one-stop-shop/index_${lang}.htm`
    },
    ossHelpText () {
      const helpText = this.$t('onboarding.accountDetails.ossHelp')
      const learnMoreText = this.$t('onboarding.accountDetails.ossLearnMore')
      return `${helpText} <a href="${this.ossLink}" target="_blank">${learnMoreText}</a>`
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/views/onBoarding/AccountDetailsOwner.vue